// Alvarás do condomínio (P-20).
//
// O cálculo dos alvarás aplicáveis já estava certo: cruza `alvaras_templates`
// com o estado e a cidade do condomínio. O que faltava era gravar — a tela era
// um checklist somente-leitura, com um modal que só mostrava o que já existia.
//
// Na prática todo alvará ficava "pendente" para sempre, porque não havia como
// informar que ele foi emitido. E o painel conta pendência a partir desta mesma
// tabela: um condomínio 100% regular aparecia com a lista inteira em aberto.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useJanelaDeAlerta } from '@/lib/notificacoes';
import { ateOuIgual, hojeISO, MENSAGEM_DATA } from '@/lib/datas';
import {
  enviarDocumento,
  gravarAlvara,
  gravarItensDoAlvara,
  lerAlvaras,
  lerItensDoAlvara,
  removerDocumento,
  urlDoDocumento,
  type AlvaraRegistro,
  type AnexoDeAlvara,
  type SituacaoAlvara,
} from '@/lib/condominioRepo';
import {
  CLASSE_SITUACAO,
  ROTULO_SITUACAO,
} from '@/pages/condominios/detail/components/situacaoAlvara';

interface Template {
  id: string;
  nome: string;
  descricao: string;
  categoria: string;
  estados: string[];
  todasCidades: boolean;
  cidadesEspecificas: string[];
  periodicidade: string;
  obrigatorio: boolean;
  orgaoEmissor: string;
}

/** Item de infraestrutura do condomínio, para o vínculo da secao 5.2. */
interface ItemDoCondominio {
  id: string;
  nome: string;
  categoria: string;
}

interface AlvarasTabProps {
  estado: string;
  cidade: string;
  condoId: string;
}

/** Linha da tela: um template aplicável, com ou sem registro. */
interface LinhaDeAlvara extends AlvaraRegistro {
  /** Sem registro no banco — a linha existe só porque o template se aplica. */
  apenasTemplate: boolean;
}

const CLASSE_CAMPO =
  'w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm';

const formatData = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const categoriaBadge = (cat: string) => {
  const map: Record<string, string> = {
    Bombeiros: 'bg-red-50 text-red-700 border-red-200',
    Prefeitura: 'bg-accent-100 text-accent-900 border-accent-300',
    'Vigilância Sanitária': 'bg-green-50 text-green-700 border-green-200',
    Ambiental: 'bg-secondary-100 text-secondary-900 border-secondary-200',
  };
  return map[cat] || 'bg-background-100 text-foreground-700 border-background-300';
};

const iconeDaSituacao = (s: SituacaoAlvara) => {
  if (s === 'vencido') return { fundo: 'bg-red-50', icone: 'ri-error-warning-line text-red-600' };
  if (s === 'a_vencer') return { fundo: 'bg-amber-50', icone: 'ri-time-line text-amber-600' };
  if (s === 'pendente') return { fundo: 'bg-amber-50', icone: 'ri-time-line text-amber-600' };
  if (s === 'nao_se_aplica')
    return { fundo: 'bg-background-100', icone: 'ri-forbid-2-line text-foreground-400' };
  return { fundo: 'bg-primary-50', icone: 'ri-file-shield-2-line text-primary-600' };
};

interface Formulario {
  status: AlvaraRegistro['status'];
  dataEmissao: string;
  dataVencimento: string;
  numeroDocumento: string;
  motivoNaoAplica: string;
}

export default function AlvarasTab({ estado, cidade, condoId }: AlvarasTabProps) {
  const janelaAlvara = useJanelaDeAlerta('alvara');

  const [templates, setTemplates] = useState<Template[]>([]);
  const [registros, setRegistros] = useState<AlvaraRegistro[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [busca, setBusca] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroSituacao, setFiltroSituacao] = useState('');

  const [edicao, setEdicao] = useState<LinhaDeAlvara | null>(null);
  // Itens de infraestrutura do condomínio e os que este registro cobre
  // (migration 0049). Um alvará pode valer para o prédio inteiro, para itens
  // específicos, ou para os dois.
  const [itensDoCondominio, setItensDoCondominio] = useState<ItemDoCondominio[]>([]);
  const [itensVinculados, setItensVinculados] = useState<string[]>([]);
  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [anexos, setAnexos] = useState<AnexoDeAlvara[]>([]);
  const [erroFormulario, setErroFormulario] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [enviandoArquivo, setEnviandoArquivo] = useState(false);

  const fetchData = useCallback(async () => {
    if (!condoId) return;
    setLoading(true);
    setFetchError(null);
    try {
      const { data: tplData, error: tplError } = await supabase
        .from('alvaras_templates')
        .select('*')
        .eq('ativo', true);

      if (tplError) throw new Error(tplError.message);

      const aplicaveis: Template[] = (tplData ?? [])
        .map((row: any) => ({
          id: row.id,
          nome: row.nome,
          descricao: row.descricao || '',
          categoria: row.categoria,
          estados: row.estados || [],
          todasCidades: row.todas_cidades ?? true,
          cidadesEspecificas: row.cidades_especificas || [],
          periodicidade: row.periodicidade || 'anual',
          obrigatorio: row.obrigatorio ?? true,
          orgaoEmissor: row.orgao_emissor || '',
        }))
        .filter((tpl) => {
          if (!tpl.estados.includes(estado)) return false;
          if (tpl.todasCidades) return true;
          return tpl.cidadesEspecificas.some((c) => c.toLowerCase() === cidade.toLowerCase());
        });

      setTemplates(aplicaveis);
      setRegistros(await lerAlvaras(condoId, janelaAlvara));
    } catch (e: any) {
      setFetchError(e?.message || 'Erro ao carregar alvarás');
    } finally {
      setLoading(false);
    }
  }, [condoId, estado, cidade, janelaAlvara]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Itens de infraestrutura do condomínio, para o vínculo da secao 5.2. Carrega
  // uma vez: a lista muda pela aba Infraestrutura, não por aqui.
  useEffect(() => {
    if (!condoId) return;
    let cancelado = false;

    void supabase
      .from('infraestrutura')
      .select('id, nome, categoria')
      .eq('condominio_id', condoId)
      .order('nome')
      .then(({ data }) => {
        if (!cancelado) setItensDoCondominio((data ?? []) as ItemDoCondominio[]);
      });

    return () => {
      cancelado = true;
    };
  }, [condoId]);

  // Template aplicável + registro existente. Registro sem template aplicável
  // (licença avulsa, ou template que deixou de valer para a cidade) continua na
  // lista: o documento existe, e escondê-lo seria perder o vencimento de vista.
  const linhas = useMemo<LinhaDeAlvara[]>(() => {
    const porTemplate = new Map(
      registros.filter((r) => r.templateId).map((r) => [r.templateId as string, r]),
    );

    const doTemplate = templates.map((tpl): LinhaDeAlvara => {
      const existente = porTemplate.get(tpl.id);
      if (existente) return { ...existente, apenasTemplate: false };

      return {
        id: `pendente-${tpl.id}`,
        condominioId: condoId,
        templateId: tpl.id,
        nome: tpl.nome,
        descricao: tpl.descricao,
        categoria: tpl.categoria,
        status: 'pendente',
        situacao: 'pendente',
        periodicidade: tpl.periodicidade,
        obrigatorio: tpl.obrigatorio,
        orgaoEmissor: tpl.orgaoEmissor,
        dataEmissao: null,
        dataVencimento: null,
        numeroDocumento: null,
        motivoNaoAplica: null,
        anexos: [],
        apenasTemplate: true,
      };
    });

    const idsDeTemplate = new Set(templates.map((t) => t.id));
    const forasDoCatalogo = registros
      .filter((r) => !r.templateId || !idsDeTemplate.has(r.templateId))
      .map((r): LinhaDeAlvara => ({ ...r, apenasTemplate: false }));

    return [...doTemplate, ...forasDoCatalogo].sort((a, b) => a.nome.localeCompare(b.nome));
  }, [templates, registros, condoId]);

  const filtrados = linhas.filter(
    (a) =>
      a.nome.toLowerCase().includes(busca.toLowerCase()) &&
      (!filtroCategoria || a.categoria === filtroCategoria) &&
      (!filtroSituacao || a.situacao === filtroSituacao),
  );

  const categoriasDisponiveis = [...new Set(linhas.map((a) => a.categoria))].filter(Boolean).sort();

  const stats = {
    total: linhas.length,
    vigente: linhas.filter((a) => a.situacao === 'vigente').length,
    a_vencer: linhas.filter((a) => a.situacao === 'a_vencer').length,
    pendente: linhas.filter((a) => a.situacao === 'pendente').length,
    vencido: linhas.filter((a) => a.situacao === 'vencido').length,
  };

  const abrirEdicao = (linha: LinhaDeAlvara) => {
    setEdicao(linha);
    setFormulario({
      status: linha.status,
      dataEmissao: linha.dataEmissao ?? '',
      dataVencimento: linha.dataVencimento ?? '',
      numeroDocumento: linha.numeroDocumento ?? '',
      motivoNaoAplica: linha.motivoNaoAplica ?? '',
    });
    setAnexos(linha.anexos);
    setErroFormulario(null);

    // Linha que ainda é só template não tem registro no banco, e portanto não
    // tem vínculo a ler.
    if (linha.apenasTemplate) {
      setItensVinculados([]);
      return;
    }
    void lerItensDoAlvara(linha.id)
      .then(setItensVinculados)
      .catch(() => setItensVinculados([]));
  };

  const fecharEdicao = () => {
    setEdicao(null);
    setFormulario(null);
    setAnexos([]);
    setItensVinculados([]);
    setErroFormulario(null);
  };

  /**
   * Grava e devolve o id.
   *
   * O upload precisa do id para montar o caminho no bucket, e um alvará que
   * ainda é só template não tem id. Por isso o registro é criado antes do
   * arquivo — e não o contrário, que deixaria arquivo órfão no storage.
   */
  const garantirRegistro = async (): Promise<string> => {
    if (!edicao || !formulario) throw new Error('Nada para gravar.');

    const id = await gravarAlvara({
      id: edicao.apenasTemplate ? undefined : edicao.id,
      condominioId: condoId,
      templateId: edicao.templateId,
      nome: edicao.nome,
      descricao: edicao.descricao,
      categoria: edicao.categoria,
      status: formulario.status,
      periodicidade: edicao.periodicidade,
      obrigatorio: edicao.obrigatorio,
      orgaoEmissor: edicao.orgaoEmissor,
      dataEmissao: formulario.dataEmissao || null,
      dataVencimento: formulario.dataVencimento || null,
      numeroDocumento: formulario.numeroDocumento.trim() || null,
      motivoNaoAplica:
        formulario.status === 'nao_se_aplica' ? formulario.motivoNaoAplica.trim() || null : null,
      anexos,
    });

    // Depois do registro, porque o vínculo precisa do id — e o gatilho do banco
    // confere que cada item é do mesmo condomínio do alvará.
    await gravarItensDoAlvara(id, itensVinculados);

    return id;
  };

  const validar = (): string | null => {
    if (!formulario) return 'Nada para gravar.';

    if (formulario.status === 'vigente' && !formulario.dataVencimento) {
      return 'Informe o vencimento: sem data não há como acompanhar a renovação.';
    }
    if (formulario.status === 'nao_se_aplica' && !formulario.motivoNaoAplica.trim()) {
      return 'Explique por que este alvará não se aplica a este condomínio.';
    }
    if (
      formulario.dataEmissao &&
      formulario.dataVencimento &&
      !ateOuIgual(formulario.dataEmissao, formulario.dataVencimento)
    ) {
      return MENSAGEM_DATA.fimAntesDoInicio;
    }
    return null;
  };

  const salvar = async () => {
    const erro = validar();
    if (erro) {
      setErroFormulario(erro);
      return;
    }

    setSalvando(true);
    setErroFormulario(null);
    try {
      await garantirRegistro();
      fecharEdicao();
      await fetchData();
    } catch (e: any) {
      setErroFormulario(e?.message || 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const anexarArquivo = async (arquivo: File) => {
    if (!edicao) return;

    const erro = validar();
    if (erro) {
      setErroFormulario(erro);
      return;
    }

    setEnviandoArquivo(true);
    setErroFormulario(null);
    try {
      // Grava primeiro para ter id: o caminho no bucket é
      // `<condominio>/<alvara>/<arquivo>`, e a policy lê o condomínio dele.
      const id = await garantirRegistro();
      const anexo = await enviarDocumento('alvaras', condoId, id, arquivo);
      const novos = [...anexos, anexo];
      setAnexos(novos);

      await gravarAlvara({
        id,
        condominioId: condoId,
        templateId: edicao.templateId,
        nome: edicao.nome,
        descricao: edicao.descricao,
        categoria: edicao.categoria,
        status: formulario!.status,
        periodicidade: edicao.periodicidade,
        obrigatorio: edicao.obrigatorio,
        orgaoEmissor: edicao.orgaoEmissor,
        dataEmissao: formulario!.dataEmissao || null,
        dataVencimento: formulario!.dataVencimento || null,
        numeroDocumento: formulario!.numeroDocumento.trim() || null,
        motivoNaoAplica:
          formulario!.status === 'nao_se_aplica'
            ? formulario!.motivoNaoAplica.trim() || null
            : null,
        anexos: novos,
      });

      setEdicao({ ...edicao, id, apenasTemplate: false, anexos: novos });
      await fetchData();
    } catch (e: any) {
      setErroFormulario(e?.message || 'Não foi possível anexar o documento.');
    } finally {
      setEnviandoArquivo(false);
    }
  };

  const removerAnexo = async (anexo: AnexoDeAlvara) => {
    if (!edicao || edicao.apenasTemplate) return;
    try {
      await removerDocumento('alvaras', anexo.caminho);
      const restantes = anexos.filter((a) => a.caminho !== anexo.caminho);
      setAnexos(restantes);
      await gravarAlvara({
        id: edicao.id,
        condominioId: condoId,
        templateId: edicao.templateId,
        nome: edicao.nome,
        descricao: edicao.descricao,
        categoria: edicao.categoria,
        status: formulario!.status,
        periodicidade: edicao.periodicidade,
        obrigatorio: edicao.obrigatorio,
        orgaoEmissor: edicao.orgaoEmissor,
        dataEmissao: formulario!.dataEmissao || null,
        dataVencimento: formulario!.dataVencimento || null,
        numeroDocumento: formulario!.numeroDocumento.trim() || null,
        motivoNaoAplica: formulario!.motivoNaoAplica.trim() || null,
        anexos: restantes,
      });
      await fetchData();
    } catch (e: any) {
      setErroFormulario(e?.message || 'Não foi possível remover o anexo.');
    }
  };

  const abrirAnexo = async (anexo: AnexoDeAlvara) => {
    const url = await urlDoDocumento('alvaras', anexo.caminho);
    if (url) window.open(url, '_blank', 'noopener');
    else setErroFormulario('Não foi possível abrir o documento.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Carregando alvarás...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center gap-4 py-20 text-center">
        <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-red-500 text-2xl"></i>
        </div>
        <h3 className="text-lg font-semibold text-foreground-900">Erro ao carregar alvarás</h3>
        <p className="text-sm text-foreground-600 max-w-md">{fetchError}</p>
        <button
          type="button"
          onClick={fetchData}
          className="px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-base font-semibold text-foreground-900">Alvarás e Licenças</h3>
        <p className="text-sm text-foreground-500 mt-0.5">
          {stats.total} alvarás aplicáveis para {cidade}, {estado}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Aplicáveis', valor: stats.total, cor: 'text-foreground-900' },
          { label: 'Vigentes', valor: stats.vigente, cor: 'text-green-700' },
          { label: 'A vencer', valor: stats.a_vencer, cor: 'text-amber-700' },
          { label: 'Pendentes', valor: stats.pendente, cor: 'text-foreground-700' },
          { label: 'Vencidos', valor: stats.vencido, cor: 'text-red-700' },
        ].map((card) => (
          <div
            key={card.label}
            className="bg-background-50 border border-background-200/70 rounded-lg p-4"
          >
            <p className="text-xs font-medium text-foreground-500">{card.label}</p>
            <p className={`text-2xl font-bold mt-1 ${card.cor}`}>{card.valor}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <i className="ri-search-line text-foreground-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Buscar alvará..."
            aria-label="Buscar alvará"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
          />
        </div>
        <select
          aria-label="Filtrar por categoria"
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value)}
          className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
        >
          <option value="">Todas as categorias</option>
          {categoriasDisponiveis.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          aria-label="Filtrar por situação"
          value={filtroSituacao}
          onChange={(e) => setFiltroSituacao(e.target.value)}
          className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
        >
          <option value="">Todas as situações</option>
          {(Object.keys(ROTULO_SITUACAO) as SituacaoAlvara[]).map((s) => (
            <option key={s} value={s}>
              {ROTULO_SITUACAO[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-100/50">
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Alvará
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Categoria
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">
                  Periodicidade
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                  Vencimento
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Situação
                </th>
                <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/50">
              {filtrados.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-foreground-400">
                    Nenhum alvará encontrado para {cidade}, {estado}.
                  </td>
                </tr>
              ) : (
                filtrados.map((a) => {
                  const visual = iconeDaSituacao(a.situacao);
                  return (
                    <tr key={a.id} className="hover:bg-background-100/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${visual.fundo}`}
                          >
                            <i className={`${visual.icone} text-sm`}></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground-800 truncate max-w-[260px]">
                              {a.nome}
                            </p>
                            <p className="text-xs text-foreground-500 truncate">
                              {a.orgaoEmissor || '—'}
                              {a.anexos.length > 0 && (
                                <span className="ml-2">
                                  <i className="ri-attachment-2"></i> {a.anexos.length}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${categoriaBadge(a.categoria)}`}
                        >
                          {a.categoria || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-sm text-foreground-600 capitalize">
                          {a.periodicidade}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span
                          className={`text-sm ${!a.dataVencimento ? 'text-foreground-400' : ''}`}
                        >
                          {formatData(a.dataVencimento)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${CLASSE_SITUACAO[a.situacao]}`}
                        >
                          {ROTULO_SITUACAO[a.situacao]}
                        </span>
                        {a.obrigatorio && (
                          <span className="ml-1.5 text-[10px] text-red-500 font-medium">Obrig</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => abrirEdicao(a)}
                          className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors ml-auto"
                        >
                          <i className={a.apenasTemplate ? 'ri-add-line' : 'ri-edit-line'}></i>
                          {a.apenasTemplate ? 'Registrar' : 'Editar'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Registro do alvará */}
      {edicao && formulario && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[6vh] px-4 pb-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={fecharEdicao}
            aria-hidden="true"
          ></div>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-alvara"
            className="relative bg-background-50 rounded-lg shadow-xl w-full max-w-xl max-h-[88vh] overflow-y-auto"
          >
            <div className="px-5 py-4 border-b border-background-200/70 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3
                  id="titulo-alvara"
                  className="text-base font-semibold text-foreground-900 truncate"
                >
                  {edicao.nome}
                </h3>
                <p className="text-xs text-foreground-500">{edicao.orgaoEmissor || '—'}</p>
              </div>
              <button
                type="button"
                onClick={fecharEdicao}
                aria-label="Fechar"
                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4">
              {edicao.descricao && (
                <p className="text-sm text-foreground-600">{edicao.descricao}</p>
              )}

              <div>
                <label
                  htmlFor="alvara-status"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  Situação declarada
                </label>
                <select
                  id="alvara-status"
                  value={formulario.status}
                  onChange={(e) =>
                    setFormulario({
                      ...formulario,
                      status: e.target.value as AlvaraRegistro['status'],
                    })
                  }
                  className={CLASSE_CAMPO}
                >
                  <option value="pendente">Pendente — ainda não obtido</option>
                  <option value="vigente">Obtido</option>
                  <option value="nao_se_aplica">Não se aplica a este condomínio</option>
                </select>
                <p className="text-xs text-foreground-500 mt-1.5">
                  &quot;Vencido&quot; não é escolhido aqui: sai do vencimento, e a tela recalcula
                  todo dia.
                </p>
              </div>

              {formulario.status === 'nao_se_aplica' ? (
                <div>
                  <label
                    htmlFor="alvara-motivo"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Por que não se aplica?
                  </label>
                  <textarea
                    id="alvara-motivo"
                    rows={2}
                    value={formulario.motivoNaoAplica}
                    onChange={(e) =>
                      setFormulario({ ...formulario, motivoNaoAplica: e.target.value })
                    }
                    placeholder="Edificação sem elevador, sem central de gás..."
                    className={`${CLASSE_CAMPO} resize-y`}
                  />
                  <p className="text-xs text-foreground-500 mt-1.5">
                    Dispensar uma exigência é decisão de alguém. Sem o motivo, ninguém sabe depois
                    se foi análise ou desistência.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="alvara-numero"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Número do documento
                    </label>
                    <input
                      id="alvara-numero"
                      type="text"
                      value={formulario.numeroDocumento}
                      onChange={(e) =>
                        setFormulario({ ...formulario, numeroDocumento: e.target.value })
                      }
                      className={CLASSE_CAMPO}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="alvara-emissao"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Emissão
                    </label>
                    <input
                      id="alvara-emissao"
                      type="date"
                      max={hojeISO()}
                      value={formulario.dataEmissao}
                      onChange={(e) =>
                        setFormulario({ ...formulario, dataEmissao: e.target.value })
                      }
                      className={CLASSE_CAMPO}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="alvara-vencimento"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Vencimento
                      {formulario.status === 'vigente' && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    <input
                      id="alvara-vencimento"
                      type="date"
                      value={formulario.dataVencimento}
                      onChange={(e) =>
                        setFormulario({ ...formulario, dataVencimento: e.target.value })
                      }
                      className={CLASSE_CAMPO}
                    />
                    <p className="text-xs text-foreground-500 mt-1.5">
                      Alvará obtido sem vencimento some do alerta e some da pendência: é a forma
                      silenciosa de nunca mais ser cobrado.
                    </p>
                  </div>
                </div>
              )}

              {/* A que este registro se aplica (secao 5.2) */}
              {formulario.status !== 'nao_se_aplica' && itensDoCondominio.length > 0 && (
                <div className="pt-4 border-t border-background-200/70">
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">
                    Itens cobertos por este documento
                  </h4>
                  <p className="text-xs text-foreground-500 mb-3">
                    Deixe em branco quando o alvará cobre o condomínio inteiro. Marque os itens
                    quando ele é específico — um laudo de elevadores por torre, por exemplo.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {itensDoCondominio.map((item) => {
                      const marcado = itensVinculados.includes(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          aria-pressed={marcado}
                          onClick={() =>
                            setItensVinculados((atuais) =>
                              marcado
                                ? atuais.filter((id) => id !== item.id)
                                : [...atuais, item.id],
                            )
                          }
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            marcado
                              ? 'bg-primary-500 text-background-50 border-primary-500'
                              : 'bg-background-50 text-foreground-700 border-background-300'
                          }`}
                        >
                          {item.nome}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Documento digitalizado */}
              <div className="pt-4 border-t border-background-200/70">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h4 className="text-sm font-semibold text-foreground-900">
                    Documento digitalizado
                  </h4>
                  <label
                    className={`whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-medium transition-colors ${
                      enviandoArquivo
                        ? 'opacity-60 cursor-not-allowed text-foreground-400'
                        : 'text-foreground-700 hover:bg-background-100 cursor-pointer'
                    }`}
                  >
                    <i
                      className={
                        enviandoArquivo ? 'ri-loader-4-line animate-spin' : 'ri-upload-2-line'
                      }
                    ></i>
                    {enviandoArquivo ? 'Enviando...' : 'Anexar'}
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png"
                      className="hidden"
                      disabled={enviandoArquivo}
                      onChange={(e) => {
                        const arquivo = e.target.files?.[0];
                        e.target.value = '';
                        if (arquivo) void anexarArquivo(arquivo);
                      }}
                    />
                  </label>
                </div>

                {anexos.length === 0 ? (
                  <p className="text-sm text-foreground-500">
                    Nenhum documento anexado. PDF, JPG ou PNG de até 10 MB.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {anexos.map((anexo) => (
                      <li
                        key={anexo.caminho}
                        className="flex items-center justify-between gap-3 rounded-md border border-background-200/70 px-3 py-2"
                      >
                        <button
                          type="button"
                          onClick={() => void abrirAnexo(anexo)}
                          className="flex items-center gap-2 min-w-0 text-left"
                        >
                          <i className="ri-file-text-line text-foreground-500"></i>
                          <span className="text-sm text-foreground-800 truncate hover:text-primary-600">
                            {anexo.nome}
                          </span>
                          <span className="text-xs text-foreground-400 whitespace-nowrap">
                            {Math.max(1, Math.round(anexo.tamanho / 1024))} KB
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => void removerAnexo(anexo)}
                          aria-label={`Remover ${anexo.nome}`}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors flex-shrink-0"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {erroFormulario && (
                <p className="text-sm text-red-700 flex items-start gap-1.5">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  {erroFormulario}
                </p>
              )}
            </div>

            <div className="px-5 py-4 border-t border-background-200/70 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={fecharEdicao}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={salvar}
                disabled={salvando}
                className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
